import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { AuthorizationService } from '@core/authorization/services/authorization.service';
import { PostLoginActionHandlerService } from '@core/sso/services/post-login-action-handler.service';
import { FormControl, FormGroup } from '@angular/forms';
import { LoginStep } from '@pages/login/models/login-step.enum';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LoginFacade } from '@pages/login/facades/login.facade';
import { LoginStatusFacade } from '@core/login-status/facades/login-status.facade';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserInfoFacade } from '@core/user-info/facades/user-info.facade';
import { BannerComponent, BannerType, inputValidators, LinkComponent, userNameValidators } from '@kaseya-design-components';
import { LoginConfigurationComponent } from '@pages/login/components/login-configuration/login-configuration.component';
import { LoginFlowComponent } from '@pages/login/components/login-flow/login-flow.component';
import { ConfigurationFormGroup } from '@pages/login/models/login-configuration/configuration-form-group.interface';
import { PostLoginWorkflow } from '@core/sso/models/post-login-workflow.enum';
import { CurrentLoginFlowService } from '@core/sso/services/current-login-flow.service';
import { ThemedImageComponent } from '@design/themed-image/components/themed-image/themed-image.component';
import { TranslatePipe } from '@ngx-translate/core';
import { LogInWithThirdPartySsoFacade } from '@pages/login/facades/log-in-with-third-party-sso.facade';
import { asNonNil } from '@kaseya-utils';
import { helpLinks } from '@layout/help-menu/models/help-link.interface';

@Component({
  selector: 'itc-login-page',
  templateUrl: 'login-page.component.html',
  styleUrl: 'login-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoginConfigurationComponent, LoginFlowComponent, ThemedImageComponent, TranslatePipe, BannerComponent, LinkComponent],
})
export class LoginPageComponent implements OnInit, OnDestroy {
  private readonly authorizationService = inject(AuthorizationService);
  private readonly postLoginActionHandlerService = inject(PostLoginActionHandlerService);
  private readonly userInfoFacade = inject(UserInfoFacade);
  private readonly router = inject(Router);
  private readonly loginFacade = inject(LoginFacade);
  private readonly loginStatusFacade = inject(LoginStatusFacade);
  private readonly logInWithThirdPartySsoFacade = inject(LogInWithThirdPartySsoFacade);

  private readonly destroyRef = inject(DestroyRef);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly currentLoginWorkflowService = inject(CurrentLoginFlowService);
  private readonly currentLoginWorkflow = this.activatedRoute.snapshot.data.workflow as PostLoginWorkflow | undefined;

  readonly companyName = input<string>();
  readonly uuid = input<string>();

  readonly LoginStep = LoginStep;
  readonly BannerType = BannerType;
  readonly statusPageUrl = helpLinks.status.url;
  readonly isUserLoggedIn = signal(this.authorizationService.isAuthorized());
  readonly currentStep = this.loginFacade.currentStep;
  readonly isLoginDegradationBannerEnabled = this.loginStatusFacade.isLoginDegradationBannerEnabled;
  configurationForm?: FormGroup<ConfigurationFormGroup>;

  constructor() {
    this.loginStatusFacade.loadLoginStatus();
    this.currentLoginWorkflowService.currentLoginFlow.set(this.currentLoginWorkflow);
    this.handleRedirectToTheSameRoute();
    this.setIsCompanyNamePrefilled();
    this.handleLoginConfigurationLoaded();
  }

  ngOnInit(): void {
    this.postLoginActionHandlerService.setSsoAction(this.activatedRoute.snapshot);
    this.createLoginForm();
  }

  ngOnDestroy(): void {
    this.loginFacade.clearState();
  }

  get organizationNameControl(): FormControl | undefined {
    return this.configurationForm?.controls.organizationName;
  }

  private handleRedirectToTheSameRoute(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.isUserLoggedIn.set(this.authorizationService.isAuthorized()));
  }

  private createLoginForm(): void {
    this.configurationForm = new FormGroup({
      username: new FormControl('', { nonNullable: true, validators: userNameValidators }),
      organizationName: new FormControl(this.companyName() ?? this.userInfoFacade.organizationName(), {
        nonNullable: true,
        validators: [inputValidators.required, inputValidators.notEmptyOrWhiteSpaces],
      }),
    });
  }

  private setIsCompanyNamePrefilled(): void {
    effect(() => {
      if (this.companyName() && this.uuid()) {
        this.organizationNameControl?.patchValue(this.companyName());
      } else {
        this.organizationNameControl?.reset();
      }
      this.organizationNameControl?.updateValueAndValidity();
    });
  }

  private handleLoginConfigurationLoaded(): void {
    this.loginFacade.loginConfigurationLoaded$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const loginConfigurationResponse = this.loginFacade.loginConfiguration();

      if (!loginConfigurationResponse) {
        return;
      }

      if (loginConfigurationResponse.isSsoEnabled && loginConfigurationResponse.isSsoRequired) {
        this.logInWithThirdPartySsoFacade.logInWithThirdPartySso(asNonNil(loginConfigurationResponse.samlLoginUrl));
        return;
      }

      this.loginFacade.goToLoginFlowStep();
    });
  }
}
